import { useState, useEffect, useRef, useCallback } from "react";
import { safeGetUserMedia } from "@/lib/browserCapabilities";
import { useParams, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { useSocket } from "@/hooks/useSocket";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Shield, MapPin, Clock, User, ChevronRight, CheckCircle2,
  Navigation, Eye, FileCheck, AlertTriangle, Phone, Camera,
  Loader2, Radio, ThumbsUp, Siren, XCircle, ImageIcon, ScanFace
} from "lucide-react";

// ─── face-api.js lazy load ───
let faceapi: any = null;

// ─── Status flow for agent actions ───
const STATUS_FLOW = [
  { key: "dispatched", label: "Acionado", icon: Siren, color: "text-amber-400", bg: "bg-amber-500/20" },
  { key: "accepted", label: "Aceito", icon: ThumbsUp, color: "text-blue-400", bg: "bg-blue-500/20" },
  { key: "en_route", label: "Em Deslocamento", icon: Navigation, color: "text-cyan-400", bg: "bg-cyan-500/20" },
  { key: "on_scene", label: "Em Atendimento", icon: Eye, color: "text-purple-400", bg: "bg-purple-500/20" },
  { key: "monitoring", label: "Monitorando", icon: Radio, color: "text-indigo-400", bg: "bg-indigo-500/20" },
  { key: "resolved", label: "Resolvido", icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/20" },
] as const;

function getStatusIndex(status: string): number {
  const idx = STATUS_FLOW.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
}

const CLASSIFICATION_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  suspect_identified: { label: "AGRESSOR IDENTIFICADO", color: "text-red-400", bgColor: "bg-red-600/20 border-red-600/30" },
  victim_identified: { label: "VÍTIMA IDENTIFICADA", color: "text-amber-400", bgColor: "bg-amber-600/20 border-amber-600/30" },
  monitored_person: { label: "PESSOA MONITORADA", color: "text-blue-400", bgColor: "bg-blue-600/20 border-blue-600/30" },
  under_analysis: { label: "EM ANÁLISE", color: "text-slate-400", bgColor: "bg-slate-600/20 border-slate-600/30" },
  confirmed_incident: { label: "INCIDENTE CONFIRMADO", color: "text-red-400", bgColor: "bg-red-600/20 border-red-600/30" },
};

// ─── Camera status for UI feedback ───
type CameraStatus =
  | "idle"
  | "requesting_permission"
  | "initializing"
  | "active"
  | "scanning"
  | "person_detected"
  | "victim_identified"
  | "aggressor_identified"
  | "identified"
  | "error";

const CAMERA_STATUS_LABELS: Record<CameraStatus, { label: string; color: string; icon?: string }> = {
  idle: { label: "", color: "" },
  requesting_permission: { label: "Solicitando permissão da câmera...", color: "text-amber-400" },
  initializing: { label: "Inicializando câmera...", color: "text-blue-400" },
  active: { label: "Câmera ativa", color: "text-green-400" },
  scanning: { label: "Analisando imagem...", color: "text-cyan-400" },
  person_detected: { label: "Pessoa detectada", color: "text-purple-400" },
  victim_identified: { label: "Vítima identificada", color: "text-amber-400" },
  aggressor_identified: { label: "Agressor identificado", color: "text-red-400" },
  identified: { label: "Pessoa identificada", color: "text-green-400" },
  error: { label: "Erro na câmera", color: "text-red-400" },
};

export default function AgentEvent() {
  const { eventId } = useParams<{ eventId: string }>();
  const searchString = useSearch();
  const id = parseInt(eventId || "0");
  const { socket, connected } = useSocket();
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  // ─── Camera state ───
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>("idle");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<{
    matched: boolean;
    personName: string | null;
    classification: string;
    label: string;
    confidence: number;
  } | null>(null);

  // ─── Face detection state ───
  const [centralMessages, setCentralMessages] = useState<Array<{message:string;kind:string;timestamp:number}>>([]);
  const [agentMessageDraft, setAgentMessageDraft] = useState("");
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);

  // ─── Refs ───
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const detectionLoopRef = useRef<number | null>(null);
  const cooldownRef = useRef(false);
  const mountedRef = useRef(true);
  const frameCounterRef = useRef(0); // V7.4: counter for periodic frame relay to Dashboard

  // Parse token from query string
  const token = (() => {
    try {
      const params = new URLSearchParams(searchString);
      return params.get("token") || "";
    } catch {
      return "";
    }
  })();

  // Fetch event data
  const event = trpc.agent.getEventForAgent.useQuery(
    { eventId: id, token },
    { enabled: id > 0 && token.length >= 8, refetchInterval: 5000 }
  );

  const updateStatus = trpc.agent.agentUpdateStatus.useMutation({
    onSuccess: () => {
      toast.success("Status atualizado com sucesso");
      setNotes("");
      event.refetch();
    },
    onError: (e: any) => toast.error(`Erro: ${e.message}`),
  });

  const ev = event.data;
  const currentDispatch = ev?.dispatches?.[0];
  const currentStatusKey = currentDispatch?.status || "dispatched";
  const currentIdx = getStatusIndex(currentStatusKey);
  const isFinished = currentStatusKey === "resolved" || currentStatusKey === "closed";

  const classInfo = CLASSIFICATION_LABELS[ev?.classification || "under_analysis"] || CLASSIFICATION_LABELS.under_analysis;

  useEffect(() => {
    if (!socket || !currentDispatch || !token || !id) return;
    socket.emit("agent:presence", {eventId:id,dispatchId:currentDispatch.id,token,agentName:currentDispatch.agentName || "Agente"});
    const onCentral=(data:any)=>{ if(Number(data?.eventId)!==Number(id))return; setCentralMessages(prev=>[...prev.slice(-49),{message:String(data.message||""),kind:data.kind||"message",timestamp:Number(data.timestamp||Date.now())}]); if(data.kind==='signal') toast.warning(`Central: ${data.message}`,{duration:8000}); else toast.info(`Central: ${data.message}`,{duration:6000}); };
    socket.on("agent:message-from-central",onCentral); return()=>{socket.off("agent:message-from-central",onCentral);};
  }, [socket,currentDispatch?.id,currentDispatch?.agentName,token,id]);


  // ─── Track component mount ───
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── Load face-api.js models ───
  const loadFaceModels = useCallback(async () => {
    if (modelsLoaded || modelsLoading) return true;
    setModelsLoading(true);
    try {
      const faceModule = await import("@vladmandic/face-api");
      faceapi = faceModule;
      const MODEL_URL = "/api/face-models";
      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      if (mountedRef.current) {
        setModelsLoaded(true);
        setModelsLoading(false);
      }
      console.log("[AgentCamera] Face models loaded successfully");
      return true;
    } catch (err) {
      console.error("[AgentCamera] Error loading face models:", err);
      if (mountedRef.current) setModelsLoading(false);
      return false;
    }
  }, [modelsLoaded, modelsLoading]);

  // ─── Listen for scan results from backend ───
  useEffect(() => {
    if (!socket) return;
    const handleScanResult = (data: any) => {
      console.log("[AgentCamera] Scan result received:", data);
      if (!mountedRef.current) return;
      
      // V12: classificação vem do Banco Facial/servidor; desconhecido permanece desconhecido
      const classification = data.classification || "unknown_person";
      
      setScanResult({
        matched: data.matched || false,
        personName: data.personName || "Desconhecido",
        classification,
        label: data.label || "Possível agressor detectado",
        confidence: data.confidence || 0,
      });

      if (classification === "aggressor" || classification === "possible_aggressor") {
        setCameraStatus("aggressor_identified");
        if (classification === "aggressor") {
          toast.error(`⚠️ AGRESSOR CONFIRMADO: ${data.personName}`);
        } else {
          toast.warning(`⚠️ POSSÍVEL AGRESSOR DETECTADO`);
        }
      } else if (classification === "victim") {
        setCameraStatus("victim_identified");
        toast.warning(`VÍTIMA IDENTIFICADA: ${data.personName}`);
      } else if (classification === "unknown_person") {
        setCameraStatus("person_detected");
        toast.info("Pessoa não identificada no Banco Facial — confirme visualmente");
      } else {
        setCameraStatus("identified");
        toast.success(`Pessoa identificada: ${data.personName}`);
      }

      // Reset after 8 seconds
      setTimeout(() => {
        if (mountedRef.current && cameraReady) {
          setCameraStatus("active");
          setScanResult(null);
        }
      }, 8000);
    };
    socket.on("agent:face-scan-result", handleScanResult);
    return () => { socket.off("agent:face-scan-result", handleScanResult); };
  }, [socket, cameraReady]);

  // ─── Face detection loop ───
  const startDetectionLoop = useCallback(() => {
    if (!faceapi || !videoRef.current || !canvasRef.current) return;
    console.log("[AgentCamera] Starting detection loop");

    const detect = async () => {
      if (!mountedRef.current || !cameraReady || !videoRef.current || cooldownRef.current) {
        detectionLoopRef.current = requestAnimationFrame(detect);
        return;
      }

      const video = videoRef.current;
      if (video.readyState < 2 || video.videoWidth === 0) {
        detectionLoopRef.current = requestAnimationFrame(detect);
        return;
      }

      try {
        const detections = await faceapi
          .detectAllFaces(video, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.6 }))
          .withFaceLandmarks(true)
          .withFaceDescriptors();

        // V10: Only accept faces with >60% confidence
        const validDetections = detections.filter((d: any) => d.detection.score >= 0.6);
        if (validDetections.length > 0 && !cooldownRef.current) {
          cooldownRef.current = true;
          if (mountedRef.current) setCameraStatus("person_detected");

          const detection = validDetections[0];
          const descriptor = Array.from(detection.descriptor);

          // Capture frame
          const canvas = canvasRef.current!;
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 480;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const frameBase64 = canvas.toDataURL("image/jpeg", 0.7);

            // Send to backend for matching
            if (socket && currentDispatch) {
              console.log("[AgentCamera] Sending face scan to backend");
              setCameraStatus("scanning");
              socket.emit("agent:face-scan", {
                eventId: id,
                dispatchId: currentDispatch.id,
                agentName: currentDispatch.agentName || "Agente",
                descriptor,
                frameBase64,
              });
            }
          }

          // Cooldown: 5 seconds between scans
          setTimeout(() => {
            cooldownRef.current = false;
            if (mountedRef.current && cameraReady) setCameraStatus("active");
          }, 5000);
        }
      } catch (err) {
        // Silently continue on detection errors
      }

      // V7.4: Send periodic frame to Dashboard for live preview (~1 frame every 3 seconds)
      frameCounterRef.current++;
      if (frameCounterRef.current % 6 === 0 && socket && id) {
        try {
          const canvas = canvasRef.current!;
          const video = videoRef.current;
          if (video && video.readyState >= 2 && video.videoWidth > 0) {
            canvas.width = Math.min(video.videoWidth, 480); // Low res for bandwidth
            canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const frameBase64 = canvas.toDataURL("image/jpeg", 0.4); // Low quality for speed
              socket.emit("agent:camera-frame", {
                eventId: id,
                agentName: currentDispatch?.agentName || "Agente",
                frameBase64,
                timestamp: Date.now(),
              });
            }
          }
        } catch {}
      }

      // Continue loop at ~2fps for performance
      setTimeout(() => {
        if (mountedRef.current) {
          detectionLoopRef.current = requestAnimationFrame(detect);
        }
      }, 500);
    };

    detectionLoopRef.current = requestAnimationFrame(detect);
  }, [socket, id, currentDispatch, cameraReady]);

  // ─── Start camera (called AFTER video element is in DOM) ───
  const initializeCamera = useCallback(async () => {
    console.log("[AgentCamera] initializeCamera called");
    setCameraError(null);
    setCameraStatus("requesting_permission");

    // Log to socket
    if (socket) {
      socket.emit("agent:camera-status", {
        eventId: id,
        status: "button_clicked",
        timestamp: Date.now(),
      });
    }

    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    try {
      // Request camera permission
      let stream: MediaStream;
      try {
        stream = await safeGetUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        console.log("[AgentCamera] Got stream with environment camera");
      } catch (envErr) {
        console.warn("[AgentCamera] Environment camera failed, trying any camera:", envErr);
        stream = await safeGetUserMedia({
          video: true,
          audio: false,
        });
        console.log("[AgentCamera] Got stream with fallback camera");
      }

      streamRef.current = stream;
      setCameraStatus("initializing");

      if (socket) {
        socket.emit("agent:camera-status", {
          eventId: id,
          status: "stream_acquired",
          tracks: stream.getTracks().map(t => ({ kind: t.kind, label: t.label })),
          timestamp: Date.now(),
        });
      }

      // CRITICAL: Wait for next frame to ensure video element is rendered
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

      const video = videoRef.current;
      if (!video) {
        // This should not happen since we render video unconditionally when showCamera=true
        // But if it does, wait a bit more and retry
        console.warn("[AgentCamera] Video ref not ready, waiting 200ms...");
        await new Promise<void>(resolve => setTimeout(resolve, 200));
        const retryVideo = videoRef.current;
        if (!retryVideo) {
          throw new Error("Elemento de vídeo não encontrado após retry");
        }
        return initializeCameraWithVideo(retryVideo, stream);
      }

      return initializeCameraWithVideo(video, stream);
    } catch (err: any) {
      console.error("[AgentCamera] Camera error:", err);
      const errorMsg =
        err.name === "NotAllowedError"
          ? "Permissão de câmera negada. Toque em 'Permitir' quando solicitado."
          : err.name === "NotFoundError"
            ? "Nenhuma câmera encontrada neste dispositivo."
            : err.name === "NotReadableError"
              ? "Câmera em uso por outro aplicativo. Feche outros apps e tente novamente."
              : err.name === "OverconstrainedError"
                ? "Câmera não suporta a resolução solicitada."
                : `Erro ao acessar câmera: ${err.message || "Verifique as permissões."}`;

      if (mountedRef.current) {
        setCameraError(errorMsg);
        setCameraStatus("error");
      }
      toast.error(errorMsg);

      if (socket) {
        socket.emit("agent:camera-status", {
          eventId: id,
          status: "failed",
          error: errorMsg,
          errorName: err.name,
          timestamp: Date.now(),
        });
      }
    }
  }, [socket, id]);

  // ─── Attach stream to video element (Safari-compatible) ───
  const initializeCameraWithVideo = useCallback(async (video: HTMLVideoElement, stream: MediaStream) => {
    console.log("[AgentCamera] Attaching stream to video element");

    // Reset video element completely
    video.pause();
    video.srcObject = null;
    video.removeAttribute("src");

    // Set Safari/iOS critical attributes BEFORE assigning srcObject
    video.setAttribute("autoplay", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("muted", "");
    video.setAttribute("webkit-playsinline", "true");
    video.muted = true;
    video.playsInline = true;
    (video as any).webkitPlaysInline = true;

    // Assign stream
    video.srcObject = stream;

    // Wait for video to be playable
    const playVideo = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.warn("[AgentCamera] Play timeout after 10s, proceeding anyway");
          resolve();
        }, 10000);

        const cleanup = () => {
          clearTimeout(timeout);
          video.removeEventListener("playing", onPlaying);
          video.removeEventListener("loadedmetadata", onMetadata);
        };

        const onPlaying = () => {
          cleanup();
          console.log(`[AgentCamera] Video playing: ${video.videoWidth}x${video.videoHeight}`);
          resolve();
        };

        const onMetadata = () => {
          console.log(`[AgentCamera] Metadata loaded: ${video.videoWidth}x${video.videoHeight}`);
          video.removeEventListener("loadedmetadata", onMetadata);
          // Safari requires play() to be called after loadedmetadata
          video.play().then(() => {
            cleanup();
            console.log("[AgentCamera] Play succeeded after metadata");
            resolve();
          }).catch((playErr) => {
            console.warn("[AgentCamera] Play after metadata failed, retrying:", playErr);
            // Safari workaround: retry after short delay
            setTimeout(() => {
              video.play().then(() => {
                cleanup();
                resolve();
              }).catch((err2) => {
                cleanup();
                console.warn("[AgentCamera] Second play attempt failed:", err2);
                // Still resolve — on some Safari versions the video plays despite the error
                resolve();
              });
            }, 500);
          });
        };

        video.addEventListener("playing", onPlaying);
        video.addEventListener("loadedmetadata", onMetadata);

        // Also try immediate play (works on Chrome/Firefox)
        video.play().catch(() => {
          console.log("[AgentCamera] Immediate play failed, waiting for loadedmetadata");
        });
      });
    };

    await playVideo();

    if (mountedRef.current) {
      setCameraReady(true);
      setCameraStatus("active");
      setCapturedPhoto(null);
      setScanResult(null);
      toast.success("Câmera ativada com sucesso");
    }

    if (socket) {
      socket.emit("agent:camera-status", {
        eventId: id,
        status: "success",
        videoSize: `${video.videoWidth}x${video.videoHeight}`,
        timestamp: Date.now(),
      });
    }

    console.log("[AgentCamera] Camera fully initialized");
  }, [socket, id]);

  // ─── Start face detection when camera is ready and models loaded ───
  useEffect(() => {
    if (cameraReady && modelsLoaded && faceapi) {
      startDetectionLoop();
    }
    return () => {
      if (detectionLoopRef.current) {
        cancelAnimationFrame(detectionLoopRef.current);
        detectionLoopRef.current = null;
      }
    };
  }, [cameraReady, modelsLoaded, startDetectionLoop]);

  // ─── Auto-load face models when camera becomes ready ───
  useEffect(() => {
    if (cameraReady && !modelsLoaded && !modelsLoading) {
      loadFaceModels();
    }
  }, [cameraReady, modelsLoaded, modelsLoading, loadFaceModels]);

  // ─── Handle "Open Camera" button ───
  const handleOpenCamera = useCallback(() => {
    console.log("[AgentCamera] Open Camera button clicked");
    // Step 1: Show the camera UI (which renders the <video> element)
    setShowCamera(true);
    // Step 2: Camera initialization happens in useEffect after video is mounted
  }, []);

  // ─── Initialize camera AFTER video element is mounted ───
  useEffect(() => {
    if (showCamera && !cameraReady && cameraStatus === "idle") {
      // Give React one frame to render the video element
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          initializeCamera();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showCamera, cameraReady, cameraStatus, initializeCamera]);

  // ─── Stop camera ───
  const stopCamera = useCallback(() => {
    console.log("[AgentCamera] Stopping camera");
    if (detectionLoopRef.current) {
      cancelAnimationFrame(detectionLoopRef.current);
      detectionLoopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setCameraStatus("idle");
    setShowCamera(false);
    setScanResult(null);
    cooldownRef.current = false;
  }, []);

  // ─── Capture photo ───
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setCapturedPhoto(dataUrl);
    toast.success("Foto capturada!");
  }, []);

  // ─── Send evidence ───
  const sendEvidence = useCallback(async () => {
    if (!capturedPhoto || !socket || !currentDispatch) return;
    setUploading(true);
    try {
      socket.emit("agent:victim-found", {
        eventId: id,
        dispatchId: currentDispatch.id,
        agentName: currentDispatch.agentName || "Agente",
        frameBase64: capturedPhoto,
      });

      socket.emit("event:save-image", {
        eventId: id,
        imageBase64: capturedPhoto,
        origin: "agent_camera",
        capturedBy: currentDispatch.agentName || "Agente",
        notes: notes || "Foto capturada pelo agente em campo",
      });

      toast.success("Evidência enviada e armazenada permanentemente");
      setCapturedPhoto(null);
    } catch (err) {
      toast.error("Erro ao enviar evidência");
    } finally {
      setUploading(false);
    }
  }, [capturedPhoto, socket, id, currentDispatch, notes]);

  // ─── V7.5: Send agent GPS location to dashboard periodically ───
  useEffect(() => {
    if (!socket || !id || !currentDispatch || isFinished) return;
    let watchId: number | null = null;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          socket.emit("agent:location-update", {
            eventId: id,
            agentName: currentDispatch.agentName || "Agente",
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => { /* silently ignore GPS errors */ },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
      );
    }
    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    };
  }, [socket, id, currentDispatch, isFinished]);

  // ─── Cleanup on unmount ───
  useEffect(() => {
    return () => {
      if (detectionLoopRef.current) cancelAnimationFrame(detectionLoopRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ─── Status advance ───
  const handleAdvanceStatus = (targetStatus: string) => {
    if (!currentDispatch) {
      toast.error("Nenhum acionamento encontrado para este evento");
      return;
    }
    updateStatus.mutate({
      eventId: id,
      dispatchId: currentDispatch.id,
      token,
      status: targetStatus as any,
      notes: notes || undefined,
      agentName: currentDispatch.agentName || "Agente",
    });

    // Camera is opened only by an explicit agent tap (Safari/iOS permission requirement).
  };

  // ─── RENDER ───

  // Hidden canvas always in DOM
  const hiddenCanvas = <canvas ref={canvasRef} className="hidden" />;

  if (!id) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        {hiddenCanvas}
        <Card className="bg-slate-900 border-slate-700 max-w-sm w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <p className="text-white font-medium">Evento não encontrado</p>
            <p className="text-sm text-slate-400 mt-2">Verifique o link recebido</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (event.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        {hiddenCanvas}
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Carregando ocorrência...</p>
        </div>
      </div>
    );
  }

  if (!ev) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        {hiddenCanvas}
        <Card className="bg-slate-900 border-slate-700 max-w-sm w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-white font-medium">Ocorrência não encontrada</p>
            <p className="text-sm text-slate-400 mt-2">O link pode estar expirado ou inválido</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const nextStatusOptions = STATUS_FLOW.filter((_, i) => i > currentIdx && !isFinished);
  const statusLabel = CAMERA_STATUS_LABELS[cameraStatus];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
      {hiddenCanvas}

      {/* Header */}
      <div className={`border-b px-4 py-3 ${
        ev.classification === "suspect_identified"
          ? "bg-red-900/30 border-red-700/30"
          : ev.classification === "victim_identified"
            ? "bg-amber-900/30 border-amber-700/30"
            : "bg-purple-900/30 border-purple-700/30"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            <span className="font-bold text-white text-sm">ELLA</span>
            <Badge className={`${classInfo.bgColor} ${classInfo.color} border text-[10px] font-bold`}>
              {classInfo.label}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-red-500"}`} />
            <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
              #{ev.id}
            </Badge>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Status Stepper */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wider">Progresso do Atendimento</p>
            <div className="flex items-center justify-between overflow-x-auto pb-1">
              {STATUS_FLOW.map((step, i) => {
                const Icon = step.icon;
                const isActive = i === currentIdx;
                const isDone = i < currentIdx;
                return (
                  <div key={step.key} className="flex items-center shrink-0">
                    <div className={`flex flex-col items-center ${isActive ? "scale-110" : ""}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                        isDone ? "bg-green-500/20" : isActive ? step.bg : "bg-slate-800"
                      }`}>
                        <Icon className={`w-3.5 h-3.5 ${
                          isDone ? "text-green-400" : isActive ? step.color : "text-slate-600"
                        }`} />
                      </div>
                      <p className={`text-[8px] mt-1 text-center max-w-[45px] leading-tight ${
                        isActive ? "text-white font-medium" : isDone ? "text-green-400" : "text-slate-600"
                      }`}>{step.label}</p>
                    </div>
                    {i < STATUS_FLOW.length - 1 && (
                      <ChevronRight className={`w-3 h-3 mx-0.5 shrink-0 ${isDone ? "text-green-500" : "text-slate-700"}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Event Info Card */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardContent className="p-4 space-y-3">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${classInfo.bgColor}`}>
              <Siren className={`w-4 h-4 ${classInfo.color}`} />
              <span className={`text-sm font-bold ${classInfo.color}`}>{classInfo.label}</span>
            </div>

            <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-3">
              {ev.capturedImageUrl ? (
                <img src={ev.capturedImageUrl} alt="" className="w-16 h-16 rounded-lg object-cover border-2 border-purple-500/50" />
              ) : ev.personPhotoUrl ? (
                <img src={ev.personPhotoUrl} alt="" className="w-16 h-16 rounded-lg object-cover border-2 border-purple-500/50" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <User className="w-8 h-8 text-purple-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{ev.personName || "Pessoa não identificada"}</p>
                <p className={`text-xs ${classInfo.color} font-medium`}>
                  {ev.classification === "suspect_identified" ? "Agressor" :
                   ev.classification === "victim_identified" ? "Vítima" :
                   "Pessoa monitorada"}
                </p>
                {ev.confidenceScore && (
                  <p className="text-xs text-slate-500 mt-0.5">Confiança: {parseFloat(ev.confidenceScore).toFixed(0)}%</p>
                )}
              </div>
              {ev.personPhotoUrl && ev.capturedImageUrl && (
                <img src={ev.personPhotoUrl} alt="Ref" className="w-12 h-12 rounded-lg object-cover border border-slate-600 opacity-80" />
              )}
            </div>

            {/* Person 2 info (conflict) */}
            {ev.person2Name && (
              <div className="flex items-center gap-3 bg-red-900/20 rounded-lg p-3 border border-red-700/30">
                {ev.person2CapturedImageUrl ? (
                  <img src={ev.person2CapturedImageUrl} alt="" className="w-16 h-16 rounded-lg object-cover border-2 border-red-500/50" />
                ) : ev.person2PhotoUrl ? (
                  <img src={ev.person2PhotoUrl} alt="" className="w-16 h-16 rounded-lg object-cover border-2 border-red-500/50" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <User className="w-8 h-8 text-red-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-red-400 font-bold uppercase mb-0.5">2ª PESSOA ENVOLVIDA</p>
                  <p className="text-white font-medium text-sm truncate">{ev.person2Name || "Desconhecido"}</p>
                  <p className="text-xs text-red-300 font-medium">
                    {ev.person2Category === "restricted" ? "Agressor" : "Vítima"}
                  </p>
                  {ev.person2Confidence && (
                    <p className="text-xs text-slate-500 mt-0.5">Confiança: {parseFloat(ev.person2Confidence).toFixed(0)}%</p>
                  )}
                </div>
                {ev.person2PhotoUrl && ev.person2CapturedImageUrl && (
                  <img src={ev.person2PhotoUrl} alt="Ref" className="w-12 h-12 rounded-lg object-cover border border-red-600/50 opacity-80" />
                )}
              </div>
            )}

            {/* Location + Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Local</p>
                  <p className="text-sm text-white">{ev.sector || "Campo"}</p>
                  {ev.address && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{ev.address}</p>}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Horário</p>
                  <p className="text-sm text-white">
                    {ev.createdAt ? new Date(ev.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {ev.createdAt ? new Date(ev.createdAt).toLocaleDateString("pt-BR") : ""}
                  </p>
                </div>
              </div>
            </div>

            {/* Google Maps link */}
            {ev.latitude && ev.longitude && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${ev.latitude},${ev.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-blue-900/20 border border-blue-700/30 rounded-lg p-3 text-blue-300 hover:bg-blue-900/30 transition-colors"
              >
                <Navigation className="w-4 h-4" />
                <span className="text-sm">Navegar até o Local (Google Maps)</span>
              </a>
            )}
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* CAMERA SECTION — Video element is ALWAYS rendered when     */}
        {/* showCamera=true, BEFORE getUserMedia is called             */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!isFinished && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Câmera Operacional</p>
                {modelsLoading && (
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />
                    <span className="text-[10px] text-cyan-400">Carregando IA...</span>
                  </div>
                )}
                {modelsLoaded && cameraReady && (
                  <div className="flex items-center gap-1.5">
                    <ScanFace className="w-3 h-3 text-green-400" />
                    <span className="text-[10px] text-green-400">IA ativa</span>
                  </div>
                )}
              </div>

              {/* Camera status feedback */}
              {cameraStatus !== "idle" && (
                <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg ${
                  cameraStatus === "aggressor_identified" ? "bg-red-900/30 border border-red-700/40" :
                  cameraStatus === "victim_identified" ? "bg-amber-900/30 border border-amber-700/40" :
                  cameraStatus === "identified" ? "bg-green-900/30 border border-green-700/40" :
                  cameraStatus === "person_detected" ? "bg-purple-900/30 border border-purple-700/40" :
                  cameraStatus === "scanning" ? "bg-cyan-900/30 border border-cyan-700/40" :
                  cameraStatus === "error" ? "bg-red-900/30 border border-red-700/40" :
                  "bg-slate-800/50"
                }`}>
                  {(cameraStatus === "scanning" || cameraStatus === "requesting_permission" || cameraStatus === "initializing") && (
                    <Loader2 className={`w-4 h-4 ${statusLabel.color} animate-spin`} />
                  )}
                  {cameraStatus === "active" && (
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  )}
                  {cameraStatus === "person_detected" && (
                    <ScanFace className="w-4 h-4 text-purple-400" />
                  )}
                  {(cameraStatus === "victim_identified" || cameraStatus === "aggressor_identified" || cameraStatus === "identified") && (
                    <CheckCircle2 className="w-4 h-4 text-current" />
                  )}
                  {cameraStatus === "error" && (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                  <span className={`text-sm font-medium ${statusLabel.color}`}>{statusLabel.label}</span>
                </div>
              )}

              {/* Scan result detail */}
              {scanResult && (
                <div className={`mb-3 px-3 py-2 rounded-lg border ${
                  scanResult.classification === "aggressor" ? "bg-red-900/20 border-red-700/30" :
                  scanResult.classification === "possible_aggressor" ? "bg-orange-900/20 border-orange-700/30" :
                  scanResult.classification === "victim" ? "bg-amber-900/20 border-amber-700/30" :
                  "bg-green-900/20 border-green-700/30"
                }`}>
                  <p className={`text-sm font-bold ${
                    scanResult.classification === "aggressor" ? "text-red-400" :
                    scanResult.classification === "possible_aggressor" ? "text-orange-400" :
                    scanResult.classification === "victim" ? "text-amber-400" :
                    "text-green-400"
                  }`}>
                    {scanResult.classification === "aggressor" ? "⚠️ AGRESSOR CONFIRMADO" :
                     scanResult.classification === "possible_aggressor" ? "⚠️ POSSÍVEL AGRESSOR" :
                     scanResult.classification === "victim" ? "🛡️ VÍTIMA" :
                     "✓ IDENTIFICADO"}
                  </p>
                  <p className="text-white text-sm mt-1">{scanResult.personName}</p>
                  {scanResult.confidence > 0 && (
                    <p className="text-xs text-slate-400 mt-0.5">Confiança: {scanResult.confidence.toFixed(0)}%</p>
                  )}
                  {scanResult.classification !== "aggressor" && (
                    <button
                      onClick={() => {
                        if (socket) {
                          socket.emit("agent:flag-aggressor", {
                            eventId: id,
                            personName: scanResult.personName || "Desconhecido",
                            agentName: currentDispatch?.agentName || "Agente",
                          });
                          setScanResult(prev => prev ? { ...prev, classification: "aggressor" } : prev);
                          setCameraStatus("aggressor_identified");
                          toast.error(`${scanResult.personName || "Pessoa"} marcado como AGRESSOR`);
                        }
                      }}
                      className="mt-2 w-full text-xs py-2 rounded-lg bg-red-600/30 border border-red-500/40 text-red-300 hover:bg-red-600/50 cursor-pointer transition-colors"
                    >
                      ⚠️ Confirmar como Agressor
                    </button>
                  )}
                  {scanResult.classification === "aggressor" && (
                    <p className="mt-2 text-xs text-red-400 font-bold text-center">AGRESSOR CONFIRMADO</p>
                  )}
                </div>
              )}

              {!showCamera ? (
                /* ─── Camera NOT open yet ─── */
                <div className="space-y-2">
                  <Button
                    onClick={handleOpenCamera}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium h-14 text-lg"
                  >
                    <Camera className="w-6 h-6 mr-2" />
                    Abrir Câmera
                  </Button>
                  {cameraError && (
                    <div className="p-3 rounded-lg bg-red-900/30 border border-red-700/40">
                      <p className="text-xs text-red-300">{cameraError}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleOpenCamera}
                        className="mt-2 border-red-600/50 text-red-300 hover:bg-red-900/20 text-xs"
                      >
                        Tentar Novamente
                      </Button>
                    </div>
                  )}

                  {/* Show captured photo if camera was closed but photo exists */}
                  {capturedPhoto && (
                    <div className="space-y-2">
                      <div className="relative rounded-lg overflow-hidden">
                        <img src={capturedPhoto} alt="Captura" className="w-full rounded-lg" />
                        <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-green-600/80 backdrop-blur-sm">
                          <span className="text-[10px] text-white font-medium">CAPTURADA</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={sendEvidence}
                          disabled={uploading}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs"
                        >
                          {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                          Enviar como Evidência
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCapturedPhoto(null)}
                          className="border-slate-600 text-slate-300 text-xs"
                        >
                          Descartar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* ─── Camera OPEN: video element always rendered ─── */
                <div className="space-y-2">
                  <div className="relative rounded-lg overflow-hidden bg-black" style={{ minHeight: "240px" }}>
                    {/* VIDEO ELEMENT: Always in DOM when showCamera=true */}
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      // @ts-ignore — Safari-specific attributes
                      webkit-playsinline="true"
                      x5-playsinline="true"
                      x5-video-player-type="h5"
                      className="w-full h-full object-cover"
                      style={{
                        minHeight: "240px",
                        WebkitTransform: "translateZ(0)",
                        // Force GPU rendering on iOS
                        transform: "translateZ(0)",
                      }}
                      onLoadedData={() => console.log("[AgentCamera] Video loadeddata event fired")}
                      onError={(e) => console.error("[AgentCamera] Video error event:", e)}
                      onPlay={() => console.log("[AgentCamera] Video play event fired")}
                    />

                    {/* Live indicator */}
                    {cameraReady && (
                      <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-[10px] text-white font-medium">AO VIVO</span>
                      </div>
                    )}

                    {/* Loading overlay while initializing */}
                    {!cameraReady && cameraStatus !== "error" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
                          <p className="text-sm text-slate-300">
                            {cameraStatus === "requesting_permission" ? "Permita o acesso à câmera..." :
                             cameraStatus === "initializing" ? "Inicializando..." :
                             "Preparando câmera..."}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Error overlay */}
                    {cameraStatus === "error" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                        <div className="text-center p-4">
                          <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                          <p className="text-sm text-red-300 mb-2">{cameraError || "Erro ao abrir câmera"}</p>
                          <Button
                            size="sm"
                            onClick={() => {
                              setCameraStatus("idle");
                              setCameraError(null);
                              setShowCamera(false);
                              setTimeout(() => handleOpenCamera(), 100);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs"
                          >
                            Tentar Novamente
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Camera action buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={capturePhoto}
                      disabled={!cameraReady}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs disabled:opacity-50"
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      Capturar Foto
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={stopCamera}
                      className="border-slate-600 text-slate-300 text-xs"
                    >
                      Fechar
                    </Button>
                  </div>

                  {/* Captured photo preview */}
                  {capturedPhoto && (
                    <div className="space-y-2">
                      <div className="relative rounded-lg overflow-hidden">
                        <img src={capturedPhoto} alt="Captura" className="w-full rounded-lg" />
                        <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-green-600/80 backdrop-blur-sm">
                          <span className="text-[10px] text-white font-medium">CAPTURADA</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={sendEvidence}
                          disabled={uploading}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs"
                        >
                          {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                          Enviar como Evidência
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCapturedPhoto(null)}
                          className="border-slate-600 text-slate-300 text-xs"
                        >
                          Descartar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {!isFinished && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4 space-y-3">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Ações</p>

              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Observações / tratativa (opcional)..."
                className="bg-slate-800 border-slate-600 text-white text-sm min-h-[80px] placeholder:text-slate-500"
              />

              <div className="space-y-2">
                {currentStatusKey === "dispatched" && (
                  <Button
                    onClick={() => handleAdvanceStatus("accepted")}
                    disabled={updateStatus.isPending}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-12 text-base"
                  >
                    {updateStatus.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <><ThumbsUp className="w-5 h-5 mr-2" /> Aceitar Ocorrência</>
                    )}
                  </Button>
                )}

                {currentStatusKey === "accepted" && (
                  <Button
                    onClick={() => handleAdvanceStatus("en_route")}
                    disabled={updateStatus.isPending}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium h-12 text-base"
                  >
                    {updateStatus.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <><Navigation className="w-5 h-5 mr-2" /> Em Deslocamento</>
                    )}
                  </Button>
                )}

                {currentStatusKey === "en_route" && (
                  <Button
                    onClick={() => handleAdvanceStatus("on_scene")}
                    disabled={updateStatus.isPending}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium h-12 text-base"
                  >
                    {updateStatus.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <><Eye className="w-5 h-5 mr-2" /> Em Atendimento</>
                    )}
                  </Button>
                )}

                {currentStatusKey === "on_scene" && (
                  <>
                    <Button
                      onClick={() => handleAdvanceStatus("monitoring")}
                      disabled={updateStatus.isPending}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium h-12 text-base"
                    >
                      {updateStatus.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <><Radio className="w-5 h-5 mr-2" /> Monitorando</>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleAdvanceStatus("resolved")}
                      disabled={updateStatus.isPending}
                      variant="outline"
                      className="w-full border-green-600/50 text-green-400 hover:bg-green-900/20 h-12 text-base"
                    >
                      {updateStatus.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <><CheckCircle2 className="w-5 h-5 mr-2" /> Ocorrência Resolvida</>
                      )}
                    </Button>
                  </>
                )}

                {currentStatusKey === "monitoring" && (
                  <Button
                    onClick={() => handleAdvanceStatus("resolved")}
                    disabled={updateStatus.isPending}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium h-12 text-base"
                  >
                    {updateStatus.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <><CheckCircle2 className="w-5 h-5 mr-2" /> Ocorrência Resolvida</>
                    )}
                  </Button>
                )}

                {notes.trim() && currentStatusKey !== "dispatched" && (
                  <Button
                    onClick={() => {
                      if (!currentDispatch) return;
                      updateStatus.mutate({
                        eventId: id,
                        dispatchId: currentDispatch.id,
                        token,
                        status: currentStatusKey as any,
                        notes: notes,
                        agentName: currentDispatch.agentName || "Agente",
                      });
                    }}
                    disabled={updateStatus.isPending}
                    variant="outline"
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 h-10"
                  >
                    <FileCheck className="w-4 h-4 mr-2" />
                    Atualizar Evento (apenas nota)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Finished */}
        {isFinished && (
          <Card className="bg-green-900/20 border-green-700/30">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <p className="text-green-300 font-medium">Atendimento Concluído</p>
              <p className="text-xs text-green-400/60 mt-1">Evento encerrado com sucesso</p>
            </CardContent>
          </Card>
        )}

        {/* Timeline */}
        {ev.timeline && ev.timeline.length > 0 && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-3">Histórico</p>
              <div className="space-y-2">
                {ev.timeline.slice(0, 20).map((entry: any, i: number) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 shrink-0" />
                    <div>
                      <p className="text-slate-300">{entry.description}</p>
                      <p className="text-slate-500">
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : ""}
                        {entry.performedBy ? ` — ${entry.performedBy}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center pb-6">
          <a href="tel:190" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-red-400 transition-colors">
            <Phone className="w-3 h-3" />
            Emergência: 190
          </a>
        </div>
      </div>
    </div>
  );
}
